import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_EDGE_TOLERANCE = 1;

export function useHorizontalScrollState<TElement extends HTMLElement>() {
  const ref = useRef<TElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const children = Array.from(element.children).filter(
      (child): child is HTMLElement => child instanceof HTMLElement,
    );

    if (children.length === 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const containerRect = element.getBoundingClientRect();
    const firstChildRect = children[0].getBoundingClientRect();
    const lastChildRect = children[children.length - 1].getBoundingClientRect();

    setCanScrollLeft(
      firstChildRect.left < containerRect.left - SCROLL_EDGE_TOLERANCE,
    );
    setCanScrollRight(
      lastChildRect.right > containerRect.right + SCROLL_EDGE_TOLERANCE,
    );
  }, []);

  useEffect(() => {
    const element = ref.current;

    if (!element || typeof window === 'undefined') {
      return undefined;
    }

    const frame = window.requestAnimationFrame(updateScrollState);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateScrollState);
    const mutationObserver =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver(updateScrollState);

    resizeObserver?.observe(element);
    Array.from(element.children).forEach((child) =>
      resizeObserver?.observe(child),
    );
    mutationObserver?.observe(element, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  return {
    canScrollLeft,
    canScrollRight,
    ref,
    updateScrollState,
  };
}
